import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Lock, UserCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function Toggle({ checked, onChange, disabled, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      data-testid={testId}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
        checked ? "bg-[#0A0A0A]" : "bg-[#D4D4D4]"
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-5" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

function Row({ icon: Icon, title, desc, children }) {
  return (
    <div className="flex items-start gap-3 px-4 py-4 border-b border-[#EEE]">
      <div className="mt-0.5 text-[#0A0A0A]"><Icon size={18} /></div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#0A0A0A]">{title}</p>
        <p className="mt-0.5 text-xs text-[#777] leading-relaxed">{desc}</p>
      </div>
      <div className="shrink-0 pt-0.5">{children}</div>
    </div>
  );
}

export default function PrivacySettingsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/privacy");
        setSettings(data);
      } catch {
        navigate("/login");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const patch = async (payload) => {
    setSaving(true);
    const prev = settings;
    setSettings((s) => ({ ...s, ...payload }));
    try {
      const { data } = await api.patch("/me/privacy", payload);
      setSettings(data);
      toast.success(t("privacy.saved", { defaultValue: "Gespeichert" }));
    } catch (e) {
      setSettings(prev);
      toast.error(e.response?.data?.detail || t("privacy.save_failed", { defaultValue: "Konnte nicht gespeichert werden" }));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#999]" /></div>;
  }

  return (
    <div data-testid="privacy-settings-page" className="w-full max-w-2xl 2xl:max-w-full 2xl:px-6 mx-auto 2xl:mx-0 py-6">
      <div className="flex items-center gap-3 px-4 mb-2">
        <button data-testid="privacy-back" onClick={() => navigate(-1)} className="text-[#0A0A0A]"><ArrowLeft size={20} /></button>
        <h1 className="text-2xl font-bold text-[#0A0A0A] flex items-center gap-2 flex-1">
          <Lock size={20} /> {t("privacy.title", { defaultValue: "Privatsphäre" })}
        </h1>
      </div>

      <div className="mt-3 bg-white rounded-2xl border border-[#EEE] mx-3 overflow-hidden">
        <div className="px-4 pt-4 pb-1 text-[10px] font-bold uppercase tracking-wider text-[#999]">Social</div>
        <Row
          icon={Lock}
          title={t("privacy.social_private_title", { defaultValue: "Privates Profil" })}
          desc={t("privacy.social_private_desc", { defaultValue: "Nur bestätigte Follower sehen deine Beiträge. Neue Follower müssen freigegeben werden." })}
        >
          <Toggle testId="toggle-social-private" checked={!!settings.social_private} disabled={saving}
            onChange={(v) => patch({ social_private: v })} />
        </Row>
        <Row
          icon={UserCheck}
          title={t("privacy.social_approval_title", { defaultValue: "Follow-Anfragen bestätigen" })}
          desc={t("privacy.social_approval_desc", { defaultValue: "Neue Follower müssen von dir bestätigt werden, bevor sie dir folgen." })}
        >
          <Toggle testId="toggle-social-approval" checked={!!settings.social_follow_approval}
            disabled={saving || !!settings.social_private}
            onChange={(v) => patch({ social_follow_approval: v })} />
        </Row>
      </div>

      {settings.has_stx_profile && (
        <p className="mt-3 mx-4 text-[11px] text-[#999] leading-relaxed">
          {t("privacy.stx_note", { defaultValue: "Hinweis: Im stilltouchedX-Bereich kann dir jede:r ohne Freigabe folgen." })}
        </p>
      )}

      <button
        data-testid="privacy-open-requests"
        onClick={() => navigate("/profile/follow-requests")}
        className="mt-4 mx-3 w-[calc(100%-1.5rem)] flex items-center justify-between px-4 py-3 bg-white rounded-2xl border border-[#EEE] hover:bg-[#FAFAFA] transition-colors"
      >
        <span className="text-sm font-semibold text-[#0A0A0A] flex items-center gap-2">
          <UserCheck size={16} /> {t("privacy.open_requests", { defaultValue: "Follow-Anfragen verwalten" })}
        </span>
        <ArrowLeft size={16} className="rotate-180 text-[#999]" />
      </button>
    </div>
  );
}
